import React from 'react';
import PropTypes from 'prop-types';

import {
  Badge,
  SpacerVertical,
  SeparatorSecondary,
  Link,
  ListPrimary,
  Typography,
} from '@klarna/bubble-ui';
import { Flex } from '@klarna/mp-ui';
import { Message } from 'mage-i18n';
import { Card, analytics } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import SummaryField from './SummaryField';
import { tPrefix as tUnderwritingInfoPrefix } from '../containers/UnderwritingInfo/constants';
import { tPrefix as tBusinessConceptPrefix } from '../containers/BusinessConcept/constants';
import { tPrefix as tCompanyDetailsPrefix } from '../containers/PreAssessmentCompanyDetails/constants';
import {
  PRE_ASSESSMENT_STEP_PATHS,
  THRESHOLD_CATEGORIES,
  THRESHOLD_BUSINESS_CONCEPTS,
  MIA_BUSINESS_CONCEPTS,
} from '../constants';
import { CY_PRE_ASSESSMENT_SUBMIT_STEP_CONTAINER_IDS } from '../containers/PreAssessmentSubmitStep/constants';
import { withFeatureToggledValidation } from '../containers/withFeatureToggledValidation';

const tPrefix = 'products-fe.containers.pre_assessment_summary';

const style = {
  flexContainer: {
    display: 'flex',
  },
  linkContainer: {
    flex: '1 0 0',
    textAlign: 'right',
    alignSelf: 'center',
  },
  productChannelBadgeWrapper: {
    display: 'inline-block',
    paddingRight: '3px',
  },
};

const ANALYTICS_EVENTS = {
  editButtonClicked: new analytics.Event({
    name: 'Edit Button Clicked',
    description: 'Merchant clicked the edit button to change inserted data',
    action: analytics.EVENT_ACTION.CTA_CLICK,
    category: analytics.EVENT_CATEGORY.FLOW_STEP,
    type: analytics.EVENT_TYPE.USER_PREFERENCE,
    properties: ['section'],
    userAttributes: ['section'],
  }),
};

const EditButton = ({ editFn, path }) => (
  <>
    <SeparatorSecondary />
    <SpacerVertical extraSmall />
    <div style={style.flexContainer}>
      <div style={style.linkContainer}>
        <Link
          href=""
          textToken="text-style/text/labels/body/medium"
          onPress={e => {
            analytics.logEvent(ANALYTICS_EVENTS.editButtonClicked, { section: path });
            e.preventDefault();
            editFn(path);
          }}
          UNSAFE_props={generateCyElemId(
            `${CY_PRE_ASSESSMENT_SUBMIT_STEP_CONTAINER_IDS.BTN_EDIT}_${path}`,
            window,
          )}
        >
          <Message path={`${tPrefix}.buttons.edit`} />
        </Link>
      </div>
    </div>
  </>
);

EditButton.propTypes = {
  editFn: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
};

const UnderwritingSummary = ({
  editFn,
  yearlyAcquiredVolume,
  paymentDelay,
  paymentInterval,
  volumeByCard,
  isSubmitted,
  isSME,
  t,
}) => (
  <Card hasFooterLink hasTitle>
    <Typography textToken="text-style/headings/titles/bold/grande">
      {t(`${tUnderwritingInfoPrefix}.title`)}
    </Typography>
    <SpacerVertical extraSmall />
    <Flex column>
      <Flex>
        <SummaryField
          titleKey={`${tUnderwritingInfoPrefix}.fields.yearly_acquired_volume.label`}
          content={Number(yearlyAcquiredVolume).toLocaleString('en', { useGrouping: true })}
        />
        {isSME && (
          <SummaryField
            titleKey={`${tUnderwritingInfoPrefix}.fields.volume_by_card.label`}
            content={volumeByCard}
          />
        )}
        <SummaryField
          titleKey={`${tUnderwritingInfoPrefix}.fields.payment_interval.label`}
          content={t(`products-fe.options.payment_intervals.${paymentInterval}`)}
        />
        <SummaryField
          titleKey={`${tUnderwritingInfoPrefix}.fields.payment_delay.label`}
          content={paymentDelay}
        />
      </Flex>
      {!isSubmitted && isSME && (
        <EditButton path={PRE_ASSESSMENT_STEP_PATHS.UNDERWRITING_INFO_STEP} editFn={editFn} />
      )}
    </Flex>
  </Card>
);

UnderwritingSummary.propTypes = {
  isSubmitted: PropTypes.bool.isRequired,
  paymentDelay: PropTypes.number.isRequired,
  paymentInterval: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  volumeByCard: PropTypes.number.isRequired,
  yearlyAcquiredVolume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  editFn: PropTypes.func,
  isSME: PropTypes.bool,
};

const BusinessConceptSummary = ({ businessConcepts = [], isSubmitted, editFn, t }) => {
  const translatedBusinessConcepts = businessConcepts
    .map(c => t(`products-fe.options.business_concepts.${c}.label`))
    .join(', ');

  return (
    <Card hasFooterLink hasTitle>
      <Typography textToken="text-style/headings/titles/bold/grande">
        {t(`${tBusinessConceptPrefix}.title`)}
      </Typography>
      <SpacerVertical extraSmall />
      <Flex column>
        <Flex>
          <SummaryField
            titleKey={`${tBusinessConceptPrefix}.fields.goods_category.label`}
            content={translatedBusinessConcepts}
          />
        </Flex>
        {!isSubmitted && (
          <EditButton path={PRE_ASSESSMENT_STEP_PATHS.BUSINESS_CONCEPT_STEP} editFn={editFn} />
        )}
      </Flex>
    </Card>
  );
};

BusinessConceptSummary.propTypes = {
  editFn: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  businessConcepts: PropTypes.array,
};

const CompanyDetailsSummary = withFeatureToggledValidation('registrationId')(
  ({
    averageOrderValue,
    registrationCountry,
    registrationNumber,
    registrationAuthority,
    vatNumber,
    volumeByCard,
    operatingCountry,
    companyName,
    storeUrl,
    salesContact,
    goodsCategory,
    isSubmitted,
    isSME,
    editFn,
    t,
    forcedValidationConfig: { registrationId: forcedValidationRegistrationId = {} } = {},
  }) => (
    <Card hasFooterLink hasTitle>
      <Typography textToken="text-style/headings/titles/bold/grande">
        {t(`${tPrefix}.company_details.title`)}
      </Typography>
      <SpacerVertical extraSmall />
      <Flex column>
        <Flex>
          {!isSME && (
            <>
              <SummaryField
                titleKey={`${tUnderwritingInfoPrefix}.fields.volume_by_card.label`}
                content={volumeByCard}
              />
              {averageOrderValue && (
                <SummaryField
                  titleKey={`${tUnderwritingInfoPrefix}.fields.average_order_value.label`}
                  content={averageOrderValue}
                />
              )}
            </>
          )}
          {salesContact && (
            <SummaryField
              titleKey={`${tCompanyDetailsPrefix}.fields.sales_contact.label`}
              content={salesContact}
            />
          )}
          <SummaryField
            titleKey={`${tCompanyDetailsPrefix}.fields.registration_country.label`}
            content={t(`products-fe.countries.${registrationCountry}`, {}, registrationCountry)}
          />
          {registrationNumber && (
            <SummaryField
              title={t(
                `${tCompanyDetailsPrefix}.fields.registration_number.label`,
                forcedValidationRegistrationId.translationKey || registrationCountry,
              )}
              content={registrationNumber}
            />
          )}
          {registrationAuthority && (
            <SummaryField
              titleKey={`${tCompanyDetailsPrefix}.fields.registration_authority.label`}
              content={registrationAuthority}
            />
          )}
          {vatNumber && (
            <SummaryField
              titleKey={`${tCompanyDetailsPrefix}.fields.vat_number.label`}
              content={vatNumber}
            />
          )}
          <SummaryField
            titleKey={`${tCompanyDetailsPrefix}.fields.operating_country.label`}
            content={t(`products-fe.countries.${operatingCountry}`, {}, operatingCountry)}
          />
          <SummaryField
            titleKey={`${tCompanyDetailsPrefix}.fields.company_name.label`}
            content={companyName}
          />
          <SummaryField
            titleKey={`${tCompanyDetailsPrefix}.fields.store_url.label`}
            content={storeUrl}
          />
          <SummaryField
            titleKey={`${tCompanyDetailsPrefix}.fields.goods_category.label`}
            content={t(`products-fe.options.goods_categories.${goodsCategory}`)}
          />
        </Flex>
        {!isSubmitted && (
          <EditButton
            path={PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_COMPANY_DETAILS_STEP}
            editFn={editFn}
          />
        )}
      </Flex>
    </Card>
  ),
);

CompanyDetailsSummary.propTypes = {
  averageOrderValue: PropTypes.number.isRequired,
  companyName: PropTypes.string.isRequired,
  editFn: PropTypes.func.isRequired,
  goodsCategory: PropTypes.string.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  operatingCountry: PropTypes.string.isRequired,
  registrationAuthority: PropTypes.string.isRequired,
  registrationCountry: PropTypes.string.isRequired,
  registrationNumber: PropTypes.string.isRequired,
  storeUrl: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  volumeByCard: PropTypes.number.isRequired,
  forcedValidationConfig: PropTypes.object,
  isSME: PropTypes.bool,
  salesContact: PropTypes.string,
  vatNumber: PropTypes.string,
};

const SellingCountriesSummary = ({ all = [], editFn, main = '', isSubmitted, isSME, t }) => (
  <Card hasFooterLink hasTitle>
    <Typography textToken="text-style/headings/titles/bold/grande">
      {t(`${tPrefix}.selling_countries.title`)}
    </Typography>
    <SpacerVertical extraSmall />
    <Flex style={{ flexDirection: 'column' }}>
      <SummaryField content={all.map(c => t(`products-fe.countries.${c}`, {}, c)).join(', ')} />
      <SummaryField
        titleKey={`${tPrefix}.selling_countries.main_selling_country`}
        content={t(`products-fe.countries.${main}`, {}, main)}
      />
      {!isSubmitted && isSME && (
        <EditButton path={PRE_ASSESSMENT_STEP_PATHS.SELLING_COUNTRIES_STEP} editFn={editFn} />
      )}
    </Flex>
  </Card>
);

SellingCountriesSummary.propTypes = {
  isSubmitted: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  all: PropTypes.array,
  editFn: PropTypes.func,
  isSME: PropTypes.bool,
  main: PropTypes.string,
};

const FcaNumberSummary = ({ fcaNumber, isSubmitted, editFn, t }) => (
  <Card hasFooterLink hasTitle>
    <Typography textToken="text-style/headings/titles/bold/grande">
      {t(`${tPrefix}.fca_number.title`)}
    </Typography>
    <SpacerVertical extraSmall />
    <Flex column>
      <Flex>
        <SummaryField content={fcaNumber} />
      </Flex>
      {!isSubmitted && (
        <EditButton path={PRE_ASSESSMENT_STEP_PATHS.FCA_NUMBER_STEP} editFn={editFn} />
      )}
    </Flex>
  </Card>
);

FcaNumberSummary.propTypes = {
  editFn: PropTypes.func.isRequired,
  fcaNumber: PropTypes.string.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

const ProductsSummary = ({ products = [], isSubmitted, t, editFn, isSME }) => (
  <Card hasFooterLink hasTitle>
    <Typography textToken="text-style/headings/titles/bold/grande">
      {t(`${tPrefix}.pre_assessment_products.title`)}
    </Typography>
    <SpacerVertical medium />
    <Flex column>
      <Flex>
        {products.map(({ country = {}, products = [] }) => (
          <React.Fragment key={`${country.key}-products`}>
            <Typography textToken="text-style/text/labels/body/medium">{country.label}</Typography>
            <SpacerVertical extraSmall />
            <ListPrimary
              items={products.map(({ label, channels }) => (
                <>
                  <div>{label}</div>
                  <div>
                    {channels.map(c => (
                      <span key={c} style={style.productChannelBadgeWrapper}>
                        <Badge>
                          {t(`products-fe.containers.pre_assessment_products.channels.${c}`)}
                        </Badge>
                      </span>
                    ))}
                  </div>
                </>
              ))}
            />
            <SpacerVertical small />
          </React.Fragment>
        ))}
      </Flex>
      {!isSubmitted && isSME && (
        <EditButton path={PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_PRODUCTS_STEP} editFn={editFn} />
      )}
    </Flex>
  </Card>
);

ProductsSummary.propTypes = {
  isSubmitted: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  editFn: PropTypes.func,
  isSME: PropTypes.bool,
  products: PropTypes.array,
};

const Questionnaire = ({ hideSubtitle, title, questions }) => (
  <>
    {!hideSubtitle && (
      <Typography textToken="text-style/text/paragraphs/body/bold">
        {title.toUpperCase()}
      </Typography>
    )}
    <Flex>
      {questions.map(({ id, text, answer }) => (
        <SummaryField key={id} title={text} content={answer} />
      ))}
    </Flex>
  </>
);

Questionnaire.propTypes = {
  hideSubtitle: PropTypes.bool.isRequired,
  questions: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
};

const QuestionnaireSummary = ({ questionnaires, title, isSubmitted, editFn }) => (
  <Card hasFooterLink hasTitle>
    <Typography textToken="text-style/headings/titles/bold/grande">
      {title || (questionnaires.length === 1 && questionnaires[0].title)}
    </Typography>
    <SpacerVertical extraSmall />
    <Flex column>
      <Flex>
        {questionnaires.map((q, i) => (
          <Questionnaire
            key={`questionnaire-summary-${i}`}
            hideSubtitle={questionnaires.length === 1 && !title}
            title={q.title}
            questions={q.questions}
          />
        ))}
      </Flex>
      {!isSubmitted && questionnaires.length > 0 && (
        <EditButton
          path={PRE_ASSESSMENT_STEP_PATHS[`${questionnaires[0].id}_QUESTIONNAIRE`]}
          editFn={editFn}
        />
      )}
    </Flex>
  </Card>
);

QuestionnaireSummary.propTypes = {
  editFn: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  questionnaires: PropTypes.array,
  title: PropTypes.string,
};

const SummaryRow = ({ children }) => (
  <>
    <SpacerVertical small />
    {children}
  </>
);

SummaryRow.propTypes = {
  children: PropTypes.node,
};

export class PreAssessmentSummary extends React.Component {
  render() {
    const { t, preAssessment, selectedProducts, edit, isSME } = this.props;
    const {
      averageOrderValue,
      businessConcepts,
      salesContact,
      companyName,
      goodsCategory,
      isSubmitted,
      paymentDelay,
      paymentInterval,
      questionnaires = {},
      registrationCountry,
      registrationNumber,
      registrationAuthority,
      operatingCountry,
      sellingCountries,
      storeUrl,
      volumeByCard,
      yearlyAcquiredVolume,
      vatNumber,
      fcaNumber,
    } = preAssessment;
    const highPMLQuestionnaire = questionnaires.HIGH_PML && {
      id: 'HIGH_PML',
      ...questionnaires.HIGH_PML,
    };

    const highExposureQuestionnaires = [
      ...THRESHOLD_CATEGORIES.map(c => {
        const id = `HIGH_EXPOSURE_SEGMENT_${c}`;
        return { id, ...questionnaires[id] };
      }).filter(q => !!q.questions),
      ...THRESHOLD_BUSINESS_CONCEPTS.map(b => {
        const id = `HIGH_EXPOSURE_BUSINESS_CONCEPT_${b}`;
        return { id, ...questionnaires[id] };
      }).filter(q => !!q.questions),
    ];

    const miaQuestionnaires = MIA_BUSINESS_CONCEPTS.map(b => {
      const id = `BUSINESS_CONCEPT_${b}`;
      return { id, ...questionnaires[id] };
    }).filter(q => !!q.questions);

    return (
      <>
        {sellingCountries && (
          <SummaryRow>
            <SellingCountriesSummary
              main={sellingCountries && sellingCountries.main}
              all={sellingCountries && sellingCountries.all}
              isSubmitted={isSubmitted}
              t={t}
              editFn={edit}
              isSME={isSME}
            />
          </SummaryRow>
        )}
        {selectedProducts && (
          <SummaryRow>
            <ProductsSummary
              products={selectedProducts}
              isSubmitted={isSubmitted}
              t={t}
              editFn={edit}
              isSME={isSME}
            />
          </SummaryRow>
        )}
        <SummaryRow>
          <UnderwritingSummary
            yearlyAcquiredVolume={yearlyAcquiredVolume}
            paymentDelay={paymentDelay}
            paymentInterval={paymentInterval}
            volumeByCard={volumeByCard}
            isSubmitted={isSubmitted}
            editFn={edit}
            isSME={isSME}
            t={t}
          />
        </SummaryRow>
        <SummaryRow>
          <BusinessConceptSummary
            businessConcepts={businessConcepts}
            isSubmitted={isSubmitted}
            t={t}
            editFn={edit}
          />
        </SummaryRow>
        <SummaryRow>
          <CompanyDetailsSummary
            averageOrderValue={averageOrderValue}
            registrationCountry={registrationCountry}
            registrationAuthority={registrationAuthority}
            registrationNumber={registrationNumber}
            vatNumber={vatNumber}
            volumeByCard={volumeByCard}
            operatingCountry={operatingCountry}
            companyName={companyName}
            salesContact={salesContact}
            storeUrl={storeUrl}
            goodsCategory={goodsCategory}
            isSubmitted={isSubmitted}
            isSME={isSME}
            t={t}
            editFn={edit}
          />
        </SummaryRow>
        {fcaNumber && (
          <SummaryRow>
            <FcaNumberSummary fcaNumber={fcaNumber} t={t} editFn={edit} isSubmitted={isSubmitted} />
          </SummaryRow>
        )}
        {highPMLQuestionnaire && (
          <SummaryRow>
            <QuestionnaireSummary
              questionnaires={[highPMLQuestionnaire]}
              title="High PML"
              isSubmitted={isSubmitted}
              editFn={edit}
            />
          </SummaryRow>
        )}
        {highExposureQuestionnaires && highExposureQuestionnaires.length > 0 && (
          <SummaryRow>
            <QuestionnaireSummary
              questionnaires={highExposureQuestionnaires}
              title={t(`${tPrefix}.high_exposure_questionnaires.title`)}
              isSubmitted={isSubmitted}
              editFn={edit}
            />
          </SummaryRow>
        )}
        {miaQuestionnaires && miaQuestionnaires.length > 0 && (
          <SummaryRow>
            <QuestionnaireSummary
              questionnaires={miaQuestionnaires}
              title={t(`${tPrefix}.mia_questionnaires.title`)}
              isSubmitted={isSubmitted}
              editFn={edit}
            />
          </SummaryRow>
        )}
      </>
    );
  }
}

PreAssessmentSummary.propTypes = {
  edit: PropTypes.func.isRequired,
  preAssessment: PropTypes.object.isRequired,
  selectedProducts: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  isSME: PropTypes.bool,
};

export default PreAssessmentSummary;
